'use client';

import { cloneElement, type ReactElement } from 'react';

import type { PersonalizationEvent } from '../../core/types';
import { usePersonalizationTracker } from '../PersonalizationProvider';

type ChildProps = Record<string, unknown>;

/**
 * Props for TrackEvent component.
 *
 * @property {PersonalizationEvent} event - Personalization event to track
 * @property {string} targetEventName - DOM event name to intercept (e.g., 'onClick', 'onFocus')
 * @property {ReactElement<ChildProps>} children - Single child element to attach event handler to
 */
interface TrackEventProps {
  event: PersonalizationEvent;
  targetEventName: string;
  children: ReactElement<ChildProps>;
}

/**
 * Generic component that tracks any personalization event on a specified DOM event.
 * More flexible than TrackClick - can track on any event (hover, focus, etc.).
 *
 * Note: Only accepts a single React element child.
 *
 * @param {TrackEventProps} props - Component props
 *
 * @example
 * // Track on hover
 * <TrackEvent
 *   event={{
 *     eventType: 'view',
 *     productId: 'prod_123',
 *     context: 'product_card',
 *     metadata: { position: 1 }
 *   }}
 *   targetEventName="onMouseEnter"
 * >
 *   <ProductCard />
 * </TrackEvent>
 *
 * @example
 * // Track on focus
 * <TrackEvent
 *   event={{
 *     eventType: 'click',
 *     productId: 'prod_123',
 *     context: 'search_bar'
 *   }}
 *   targetEventName="onFocus"
 * >
 *   <input />
 * </TrackEvent>
 *
 * @example
 * // Track custom event
 * <TrackEvent
 *   event={{
 *     eventType: 'add_to_cart',
 *     productId: 'prod_123',
 *     context: 'quick_add',
 *     quantity: 1
 *   }}
 *   targetEventName="onDoubleClick"
 * >
 *   <button>Quick Add</button>
 * </TrackEvent>
 */
export function TrackEvent({ event, targetEventName, children }: TrackEventProps) {
  const tracker = usePersonalizationTracker();

  return cloneElement(children, {
    [targetEventName]: (...args: unknown[]) => {
      tracker.track(event);

      const originalHandler = children.props[targetEventName];

      if (typeof originalHandler === 'function') {
        return (originalHandler as (...a: unknown[]) => unknown)(...args);
      }
    },
  });
}
